import React, { useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import PlatformHubTabs from "../components/PlatformHubTabs";
import { api } from "../lib/api";
import OnlyFansLiveStats from "../components/OnlyFansLiveStats";
import OperationsPulse from "../components/OperationsPulse";
import { useAutoRefresh, triggerGlobalRefresh } from "../lib/autoRefresh";
import {
  Crown, TrendingUp, DollarSign, Video, RefreshCw, ExternalLink,
  Users, Eye, Heart, Settings, Save, Trash2, ToggleLeft, ToggleRight,
  Image as ImageIcon, MessageSquare, FileText, Landmark
} from "lucide-react";
import { toast } from "sonner";

const OF_BLUE = "#00AFF0";

export default function OnlyFansFreeHub() {
  const [loading, setLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [useManualData, setUseManualData] = useState(true);
  const [manualStats, setManualStats] = useState({
    subscribers: 0,
    followers: 0,
    posts_count: 0,
    total_revenue: 0,
    post_views: 0,
    messages: 0
  });
  
  // Dashboard state with auto-refresh
  const { data: stats } = useAutoRefresh(
    "/api/onlyfans/stats/free",
    null,
    ['onlyfans_free', '*'],
    { fetchOnMount: true, debounce: 200, silent: true }
  );
  
  const [extensionStats, setExtensionStats] = useState(null);

  useEffect(() => {
    loadSettings();
    loadExtensionData();
  }, []);
  
  const loadExtensionData = async () => {
    try {
      const res = await api.get("/api/platform-data/stats/onlyfans_free");
      setExtensionStats(res.data);
    } catch (e) {
      console.error("Failed to load extension stats:", e);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await api.get("/api/onlyfans/settings/free");
      if (res.data) {
        setUseManualData(res.data.use_manual_data ?? true);
        setManualStats(res.data.manual_stats || manualStats);
      }
    } catch (e) {
      console.error("Failed to load settings:", e);
    }
  };

  const saveManualStats = async () => {
    try {
      await api.post("/api/onlyfans/manual-stats/free", { stats: manualStats });
      toast.success("Manual stats saved!");
      triggerGlobalRefresh('onlyfans_free');
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save stats");
    }
  };

  const toggleDataSource = async () => {
    const newValue = !useManualData;
    setUseManualData(newValue);
    try {
      await api.post("/api/onlyfans/settings/free", {
        use_manual_data: newValue
      });
      toast.success(newValue ? "Using manual data" : "Using live sync data");
      triggerGlobalRefresh('onlyfans_free');
    } catch (e) {
      toast.error("Failed to update settings");
    }
  };

  const deleteManualData = async () => {
    if (!confirm("Delete all manual data? This will use live sync only.")) return;
    try {
      await api.delete("/api/onlyfans/manual-stats/free");
      setManualStats({
        subscribers: 0,
        followers: 0,
        posts_count: 0,
        total_revenue: 0,
        post_views: 0,
        messages: 0
      });
      setUseManualData(false);
      toast.success("Manual data deleted. Using live sync.");
      triggerGlobalRefresh('onlyfans_free');
    } catch (e) {
      toast.error("Failed to delete manual data");
    }
  };

  const manualRefresh = async () => {
    setLoading(true);
    try {
      const r = await api.post("/api/onlyfans/sync/free");
      if (r.data.ok) {
        toast.success("OnlyFans Free data synced successfully!");
      } else {
        toast.error(r.data.error || "Sync failed");
      }
      triggerGlobalRefresh('onlyfans_free');
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setLoading(false);
    }
  };

  const displayStats = useManualData 
    ? (stats && Object.keys(stats).length > 0 ? stats : manualStats)
    : (extensionStats || manualStats);

  return (
    <AppLayout
      title="OnlyFans Free Hub"
      overline="Platform CRM · Free Page · Performance"
      actions={
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSettingsOpen(!settingsOpen)}
            className="btn-ghost inline-flex items-center gap-2 backdrop-blur-xl bg-black/40 border border-white/20 text-white hover:border-purple-500">
            <Settings size={16} />
            Settings
          </button>
          <button
            onClick={manualRefresh}
            disabled={loading}
            className="btn-ghost inline-flex items-center gap-2 backdrop-blur-xl bg-black/40 border border-blue-500/50 text-blue-300 hover:border-blue-500">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            {loading ? "Syncing..." : "Sync Now"}
          </button>
          <a
            href="https://onlyfans.com/giopilosfree"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost inline-flex items-center gap-2 text-xs backdrop-blur-xl bg-black/40 border border-white/20 text-white hover:border-blue-500">
            <ExternalLink size={14} />
            Open OnlyFans Free
          </a>
        </div>
      }>
      <PlatformHubTabs platform="onlyfans_free" platformName="OnlyFans Free">
      <OperationsPulse
        title="Free audience operating pulse"
        subtitle="Audience growth, engagement, and conversion signals feeding the VIP funnel."
        accent={OF_BLUE}
        metrics={[
          { label: "Followers", value: displayStats?.followers || 0, color: "#60a5fa", detail: "free audience" },
          { label: "Subscribers", value: displayStats?.subscribers || 0, color: "#22c55e", detail: "active state" },
          { label: "Post views", value: Number(displayStats?.post_views || 0).toLocaleString(), color: "#ec4899", detail: "engagement" },
          { label: "Messages", value: displayStats?.messages || 0, color: "#f59e0b", detail: "conversation signal" },
        ]}
        gap="⚠️ Limited data: Statement rows, payout activity, retention analytics, and conversion tracking are not yet available. Backend expansion required."
      />
      
      {/* Settings Panel */}
      {settingsOpen && (
        <div className="mb-6 backdrop-blur-xl bg-purple-900/20 border border-white/10 rounded-lg p-6">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Settings size={20} className="text-purple-400" />
            OnlyFans Free Settings
          </h3>

          {/* Data Source Toggle */}
          <div className="mb-6 p-4 rounded-lg bg-black/40 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-semibold text-white">Data Source</div>
                <div className="text-sm text-zinc-400">
                  {useManualData ? "Using manual entry" : "Using live sync from OnlyFans"}
                </div>
              </div>
              <button
                onClick={toggleDataSource}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition backdrop-blur-xl bg-purple-600 hover:bg-purple-700 text-white">
                {useManualData ? <ToggleLeft size={20} /> : <ToggleRight size={20} />}
                {useManualData ? "Switch to Live" : "Switch to Manual"}
              </button>
            </div>
          </div>

          {/* Manual Stats Entry */}
          {useManualData && (
            <div className="p-4 rounded-lg bg-black/40 border border-white/10">
              <h4 className="font-semibold text-white mb-4">Manual Stats Entry</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Subscribers</label>
                  <input
                    type="number"
                    value={manualStats.subscribers}
                    onChange={(e) => setManualStats({...manualStats, subscribers: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 rounded bg-black/60 border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Followers</label>
                  <input
                    type="number"
                    value={manualStats.followers}
                    onChange={(e) => setManualStats({...manualStats, followers: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 rounded bg-black/60 border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Posts</label>
                  <input
                    type="number"
                    value={manualStats.posts_count}
                    onChange={(e) => setManualStats({...manualStats, posts_count: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 rounded bg-black/60 border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Revenue ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={manualStats.total_revenue}
                    onChange={(e) => setManualStats({...manualStats, total_revenue: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 rounded bg-black/60 border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Post Views</label>
                  <input
                    type="number"
                    value={manualStats.post_views}
                    onChange={(e) => setManualStats({...manualStats, post_views: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 rounded bg-black/60 border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Messages</label>
                  <input
                    type="number"
                    value={manualStats.messages}
                    onChange={(e) => setManualStats({...manualStats, messages: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 rounded bg-black/60 border border-white/10 text-white"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={saveManualStats}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium backdrop-blur-xl bg-green-600 hover:bg-green-700 text-white">
                  <Save size={16} />
                  Save Manual Stats
                </button>
                <button
                  onClick={deleteManualData}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium backdrop-blur-xl bg-red-600/80 hover:bg-red-700 text-white">
                  <Trash2 size={16} />
                  Delete Manual Data
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard
          icon={Users}
          label="Subscribers"
          value={displayStats?.subscribers || 0}
          color="#3b82f6"
        />
        <KPICard
          icon={Eye}
          label="Followers"
          value={displayStats?.followers || 0}
          color="#8b5cf6"
        />
        <KPICard
          icon={ImageIcon}
          label="Total Posts"
          value={displayStats?.posts_count || 0}
          color="#00AFF0"
        />
        <KPICard
          icon={DollarSign}
          label="Revenue"
          value={`$${(displayStats?.total_revenue || 0).toFixed(2)}`}
          color="#22c55e"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <KPICard
          icon={Heart}
          label="Post Views"
          value={(displayStats?.post_views || 0).toLocaleString()}
          color="#ec4899"
        />
        <KPICard
          icon={MessageSquare}
          label="Messages"
          value={displayStats?.messages || 0}
          color="#f59e0b"
        />
      </div>

      {/* Live Stats (best-effort browser-cookie scrape) */}
      <OnlyFansLiveStats account="free" accent="#22c55e" />

      {/* Management overview */}
      <div className="backdrop-blur-xl bg-purple-900/20 border border-white/10 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <ImageIcon size={24} className="text-blue-500 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-bold text-white mb-2">OnlyFans Free Audience &amp; Conversion Management</h3>
            <p className="text-sm text-zinc-400 mb-4">
              Operate the free OnlyFans audience funnel with visibility into followers, subscribers, content volume, views, messages, and available revenue. This hub is the acquisition and conversion control center that feeds the premium VIP business.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-lg p-4">
                <div className="font-semibold text-blue-400 mb-1">Audience Growth</div>
                <div className="text-zinc-400">Followers, subscribers, posts, and post views are tracked above.</div>
              </div>
              <div className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-lg p-4">
                <div className="font-semibold text-purple-400 mb-1">Fan Engagement</div>
                <div className="text-zinc-400">Messages and content response signals support the free-to-VIP funnel.</div>
              </div>
              <div className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-lg p-4">
                <div className="font-semibold text-green-400 mb-1">Conversion Workflow</div>
                <div className="text-zinc-400">Use free-page activity to identify audience opportunities for VIP conversion.</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <div className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-lg p-5">
          <div className="flex items-center gap-3 mb-2">
            <FileText size={20} className="text-blue-400" />
            <h3 className="font-semibold text-white">Available operating data</h3>
          </div>
          <p className="text-sm text-zinc-400">Subscriber, follower, post, view, message, and total-revenue metrics are the fields safely provided by the current Free hub data contract.</p>
        </div>
        <div className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-lg p-5">
          <div className="flex items-center gap-3 mb-2">
            <Landmark size={20} className="text-amber-400" />
            <h3 className="font-semibold text-white">Financial coverage gap</h3>
          </div>
          <p className="text-sm text-zinc-400">Statement rows, payout activity, retention analytics, and conversion attribution are outside the current Free hub API contract. Finance remains downstream; full hub-native reporting requires backend/data-model expansion.</p>
        </div>
      </div>
      </PlatformHubTabs>
    </AppLayout>
  );
}

function KPICard({ icon: Icon, label, value, color }) {
  return (
    <div className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-lg p-6 hover:border-purple-500/50 transition">
      <div className="flex items-center gap-3 mb-2">
        <Icon size={24} style={{ color }} />
        <div className="text-sm font-medium text-zinc-400">{label}</div>
      </div>
      <div className="text-3xl font-bold text-white">{value}</div>
    </div>
  );
}
